import React from 'react';

import type { WoundImage } from './useWoundImages';

/**
 * Photos shown inline in a wound row before the rest collapse into a `+N` tile.
 * Tune this to trade row density against how much of a wound's photo history is
 * visible at a glance; the `Photos` column resizes itself to match.
 */
export const MAX_WOUND_THUMBNAILS = 3;

const THUMBNAIL_PX = 40;
const GAP_PX = 4;
/** `TableCell` is `p-3`, so 12px of padding on each side. */
const CELL_PADDING_PX = 24;

/**
 * Width the `Photos` column needs for a full strip: every thumbnail plus the
 * `+N` tile. Derived so that changing `MAX_WOUND_THUMBNAILS` is the only edit.
 */
export const WOUND_PHOTOS_COLUMN_PX =
  (MAX_WOUND_THUMBNAILS + 1) * THUMBNAIL_PX +
  MAX_WOUND_THUMBNAILS * GAP_PX +
  CELL_PADDING_PX;

interface WoundThumbnailsProps {
  /** Every photo logged for the wound, newest first. */
  images: WoundImage[];
  /** Opens the gallery at this position in `images`. */
  onPreview: (index: number) => void;
}

const WoundThumbnails: React.FC<WoundThumbnailsProps> = ({
  images,
  onPreview,
}) => {
  if (images.length === 0) return null;

  const visible = images.slice(0, MAX_WOUND_THUMBNAILS);
  const hiddenCount = images.length - visible.length;

  return (
    <div className='flex items-center gap-1'>
      {visible.map((image, index) => (
        <button
          key={image.attachmentId}
          type='button'
          onClick={() => onPreview(index)}
          className='h-10 w-10 shrink-0 overflow-hidden rounded border cursor-zoom-in'
          aria-label={`Preview ${image.fileName}`}
        >
          <img
            src={image.url}
            alt={image.fileName}
            loading='lazy'
            className='h-full w-full object-cover'
          />
        </button>
      ))}
      {hiddenCount > 0 && (
        // Opens on the first photo the strip had to hide, so the gallery picks
        // up where the visible thumbnails left off.
        <button
          type='button'
          onClick={() => onPreview(visible.length)}
          className='h-10 w-10 shrink-0 rounded border bg-muted text-xs font-medium text-muted-foreground hover:bg-muted/70'
          aria-label={`View all ${images.length} photos`}
        >
          +{hiddenCount}
        </button>
      )}
    </div>
  );
};

export default WoundThumbnails;
